import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    window.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.#resetDesktopInlineZoom();
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (this.#shouldUseDesktopInlineZoom() && this.presentation === 'grid') {
      event.preventDefault();
      this.#toggleDesktopInlineZoom(index);
      return;
    }

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.#resetDesktopInlineZoom();
  };

  #handleResize = () => {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    this.#resetDesktopInlineZoom();
  };

  #shouldUseDesktopInlineZoom() {
    return Boolean(this.#getDesktopInlineZoomRoot()) && window.matchMedia('(min-width: 750px)').matches;
  }

  #toggleDesktopInlineZoom(index) {
    const root = this.#getDesktopInlineZoomRoot();
    if (!root) return;

    const mediaItems = this.media?.length
      ? this.media
      : Array.from(this.querySelectorAll('.media-gallery__grid > .product-media-container'));
    const targetItem = mediaItems[index];
    if (!targetItem) return;

    const isSameItemActive =
      root.classList.contains('is-desktop-zoomed') && targetItem.classList.contains('is-desktop-zoom-active');

    mediaItems.forEach((item) => item.classList.remove('is-desktop-zoom-active'));

    if (isSameItemActive) {
      this.#resetDesktopInlineZoom();
      return;
    }

    this.#setDesktopInlineZoomDetailsPosition(root);
    targetItem.classList.add('is-desktop-zoom-active');
    root.classList.add('is-desktop-zoomed');

    requestAnimationFrame(() => {
      targetItem.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }

  #resetDesktopInlineZoom() {
    const root = this.#getDesktopInlineZoomRoot();
    if (!root) return;

    root.classList.remove('is-desktop-zoomed');
    root.style.removeProperty('--desktop-zoom-details-left');
    root.style.removeProperty('--desktop-zoom-details-top');
    root.style.removeProperty('--desktop-zoom-details-width');
    root.style.removeProperty('--desktop-zoom-details-max-height');
    this.media?.forEach((item) => item.classList.remove('is-desktop-zoom-active'));
  }

  #setDesktopInlineZoomDetailsPosition(root) {
    const details = root.querySelector('.product-details');
    if (!(details instanceof HTMLElement)) return;

    const rect = details.getBoundingClientRect();
    const minBottomGap = 16;
    const maxHeight = Math.max(220, window.innerHeight - rect.top - minBottomGap);

    root.style.setProperty('--desktop-zoom-details-left', `${Math.max(0, rect.left)}px`);
    root.style.setProperty('--desktop-zoom-details-top', `${Math.max(0, rect.top)}px`);
    root.style.setProperty('--desktop-zoom-details-width', `${rect.width}px`);
    root.style.setProperty('--desktop-zoom-details-max-height', `${maxHeight}px`);
  }

  #getDesktopInlineZoomRoot() {
    return this.closest('[data-desktop-inline-zoom="true"]');
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
